import Image from 'next/image'
import Link from 'next/link'
import React, { ComponentProps } from 'react'

import { Icon } from '@/shared/components/Icon/Icon'
import { Money } from '@/shared/libs/money'
import { useFormatMoney } from '@/shared/market'
import { pxToRem } from '@/shared/styles/themes'
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'
import { Grid2 as Grid, styled, Typography } from '@mui/material'

import { Button } from '../Button/Button'

export type CheckoutCartLineProps = Omit<ComponentProps<typeof StyledLineGrid>, 'container' | 'direction' | 'alignItems' | 'justifyContent'> & {
  image: string
  name: string
  href?: string
  subtitle?: string
  overlayImage?: string
  /** Optional custom node rendered in place of the main `image`. Used by
   *  callers that want to put a richer composition (e.g. `HardcoverMockup`)
   *  into the line's image slot. The `overlayImage` thumbnail still renders
   *  on top in the bottom-right corner. */
  customImage?: React.ReactNode
  quantity: number
  maxQuantity: number
  price: Money
  compareAtPrice?: Money | null
  onUpdateQuantity?: (quantity: number) => void
  onRemove?: () => void
}

export const CheckoutCartLine: React.FC<CheckoutCartLineProps> = ({ name, href, price, compareAtPrice, quantity, onUpdateQuantity, onRemove, image, subtitle, overlayImage, customImage, ...props }) => {
  const { formatMoney } = useFormatMoney()

  const imageContent = (
    <StyledLineImage>
      {customImage ? (
        <StyledCustomImageWrapper>{customImage}</StyledCustomImageWrapper>
      ) : (
        <StyledNextImage
          src={image}
          alt={name}
          fill={true}
          unoptimized
          style={{
            objectFit: 'cover',
          }}
        />
      )}
      {overlayImage && <StyledOverlayImage src={overlayImage} alt="" width={48} height={48} unoptimized />}
    </StyledLineImage>
  )

  return (
    <StyledLineGrid container direction="row" alignItems="stretch" justifyContent="flex-start" wrap="nowrap" columns={15} {...props}>
      <StyledOrderLineImage size={{ xs: 4, sm: 'auto' }}>
        {href ? (
          <Link href={href} passHref>
            {imageContent}
          </Link>
        ) : (
          imageContent
        )}
      </StyledOrderLineImage>
      <StyledOrderLineContent>
        <Grid container direction="row" justifyContent={'space-between'} flexWrap="wrap">
          <StyledItemInfoColumn>
            {href ? (
              <Link href={href} passHref>
                <StyledItemName variant="body1" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                  {name}
                </StyledItemName>
              </Link>
            ) : (
              <StyledItemName variant="body1">
                {name}
              </StyledItemName>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {quantity > 1 && (
              <Typography variant="body2" color="text.secondary">
              Qty: {quantity}
              </Typography>
            )}
          </StyledItemInfoColumn>
          <StyledPriceColumn>
            {compareAtPrice && price && compareAtPrice.amount > price.amount && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                {formatMoney(compareAtPrice)}
              </Typography>
            )}
            <StyledPriceTypography variant="body2">
              {formatMoney(price)}
            </StyledPriceTypography>
          </StyledPriceColumn>
        </Grid>
        {onRemove && (
          <StyledOrderLineButtonsContainer>
            <StyledOrderLineButton text round onClick={onRemove} startIcon={<Icon icon={faTrash} sx={{ fontSize: pxToRem(14) }} />}>
            Remove
            </StyledOrderLineButton>
          </StyledOrderLineButtonsContainer>
        )}
      </StyledOrderLineContent>
    </StyledLineGrid >
  )
}

export const StyledItemInfoColumn = styled(Grid)`
  width: 100%;

  ${props => props.theme.breakpoints.up('sm')} {
    width: auto;
  }
`

export const StyledPriceColumn = styled(Grid)`
  text-align: right;
  width: 100%;
  height: 100%;
  margin-top: ${pxToRem(16)};

  ${props => props.theme.breakpoints.up('sm')} {
    margin-top: 0;
    width: ${pxToRem(200)};
  }
`

export const StyledOrderLineContent = styled(Grid)`
  padding-top: ${pxToRem(4)};
  min-height: 100%;

  ${props => props.theme.breakpoints.up('sm')} {
    width: 80%;
  }
`

export const StyledPriceTypography = styled(Typography)`
  color: ${props => props.theme.palette.text.primary};
`

export const StyledOrderLineButtonsContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-top: ${pxToRem(8)};
  margin-left: ${pxToRem(-12)};
  gap: ${pxToRem(20)};

  ${props => props.theme.breakpoints.up('sm')} {
    margin-top: ${pxToRem(12)};
  }
`

export const StyledOrderLineButton = styled(Button)`
  font-weight: 400;
`

export const StyledItemName = styled(Typography)`
  font-weight: 300;
`

export const StyledLineGrid = styled(Grid)`
  padding-bottom: ${pxToRem(16)};
  margin-bottom: ${pxToRem(16)};
  gap: ${pxToRem(16)};
`

export const StyledLineImage = styled('div')`
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  border-radius: ${pxToRem(12)};

  ${props => props.theme.breakpoints.up('sm')} {
    padding-bottom: 100%;
  }
`

export const StyledNextImage = styled(Image)`
  object-fit: cover;
  border-radius: ${pxToRem(12)};
  object-position: 50% 0%;
`

const StyledCustomImageWrapper = styled('div')`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${pxToRem(12)};
  overflow: hidden;

  & > * {
    max-width: 100%;
    max-height: 100%;
  }
`

const StyledOverlayImage = styled(Image)`
  position: absolute;
  bottom: ${pxToRem(6)};
  right: ${pxToRem(6)};
  width: 40%;
  height: 40%;
  object-fit: cover;
  border-radius: ${pxToRem(6)};
  border: 2px solid ${props => props.theme.palette.background.paper};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  z-index: 1;
`

export const StyledOrderLineImage = styled(Grid)`

  ${props => props.theme.breakpoints.up('sm')} {
    width: ${pxToRem(120)};
  }

  ${props => props.theme.breakpoints.up('md')} {
    width: ${pxToRem(180)};
  }

  ${props => props.theme.breakpoints.up('lg')} {
    width: ${pxToRem(220)};
  }
`
